// src/oura/components/plan/PriorityKeywords.tsx
//
// The contract's priority keywords (#3975): the 5 to 10 phrases the catalogue
// captures at the quotation discussion. They are a CONTRACT fact, not a
// setting, so they live in What was sold and are saved with the plan, through
// the same "What changed, in one line" bar as every other edit.
//
// Why the field exists at all: `priority_keywords_incomplete` blocks
// publishing the schedule, and an unpublished schedule means the merchant is
// told no dates at all ("No dates are set yet"). Until this field, nothing on
// any screen could clear that (ourteam-backend#5256).
//
// Presentational: it holds the typing, and the draft it is given holds the
// phrases. It decides nothing about saving.

import { useRef, useState } from "react";
import { PlusIcon, XIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { FOCUS_RING, TYPE } from "@oura/ui";
import { KEYWORDS_FIELD_ID, KEYWORDS_MAX, KEYWORDS_MIN, cleanKeywords, keywordsProblem } from "./planModel";

const FIELD = cn(
    TYPE.tb,
    "block h-11 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9",
);

/** A phrase a keyword can never be: the field says so instead of sending it. */
const TOO_LONG = 120;

export function PriorityKeywords({
    value,
    saved,
    onChange,
    disabled = false,
}: {
    /** The draft's phrases, in the order they were added. */
    value: readonly string[];
    /** What the contract holds now, so the field can say an edit is unsaved. */
    saved: readonly string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
}) {
    const [typed, setTyped] = useState("");
    const [said, setSaid] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const clean = cleanKeywords(value);
    const problem = keywordsProblem(value);
    const changed = JSON.stringify(clean) !== JSON.stringify(cleanKeywords(saved));

    /** Takes one phrase, or several pasted in at once ("facial, botox"). */
    const add = (raw: string) => {
        const parts = raw.split(/[,\n;]/).map((p) => p.replace(/\s+/g, " ").trim()).filter(Boolean);
        if (parts.length === 0) return;
        if (parts.some((p) => p.length > TOO_LONG)) {
            setSaid(`A keyword is a phrase, not a sentence — keep it under ${TOO_LONG} characters.`);
            return;
        }
        // What was already there, said plainly rather than dropped in silence.
        const before = new Set(clean.map((k) => k.toLowerCase()));
        const already = parts.filter((p) => before.has(p.toLowerCase()));
        setSaid(already.length > 0 ? `${already.join(", ")} ${already.length === 1 ? "is" : "are"} already on the list.` : null);
        const next = cleanKeywords([...value, ...parts]);
        if (next.length !== clean.length) onChange(next);
        setTyped("");
    };

    const remove = (phrase: string) => {
        setSaid(null);
        onChange(clean.filter((k) => k !== phrase));
    };

    return (
        // scroll-mb keeps the chips clear of the sticky save bar when focus or a
        // deep link scrolls them into view (#3989).
        <div className="min-w-0 scroll-mb-32">
            <label htmlFor={KEYWORDS_FIELD_ID} className={TYPE.lab}>
                Priority keywords
            </label>
            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                {KEYWORDS_MIN} to {KEYWORDS_MAX} phrases, agreed at the quotation. The schedule cannot be published without them.
            </p>

            {clean.length > 0 && (
                <ul className="m-0 mt-2 flex list-plain flex-wrap gap-1.5 p-0">
                    {clean.map((phrase) => (
                        <li key={phrase.toLowerCase()}>
                            <span className={cn(TYPE.tc, "inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100 py-1 pl-2.5 pr-1 text-gray-800")}>
                                <span className="min-w-0 break-words">{phrase}</span>
                                <button
                                    type="button"
                                    disabled={disabled}
                                    aria-label={`Take out ${phrase}`}
                                    onClick={() => remove(phrase)}
                                    className={cn(
                                        "inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-50",
                                        FOCUS_RING,
                                    )}
                                >
                                    <XIcon aria-hidden size={12} />
                                </button>
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                    ref={inputRef}
                    id={KEYWORDS_FIELD_ID}
                    value={typed}
                    disabled={disabled}
                    autoComplete="off"
                    maxLength={TOO_LONG + 1}
                    placeholder={clean.length === 0 ? "A phrase customers search, with the area" : "Add another"}
                    aria-describedby={`${KEYWORDS_FIELD_ID}-note`}
                    onChange={(e) => {
                        setSaid(null);
                        // A comma is how a list is pasted in; it ends the phrase.
                        if (/[,\n;]/.test(e.target.value)) add(e.target.value);
                        else setTyped(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            // Enter adds the phrase; it must not submit the plan.
                            e.preventDefault();
                            add(typed);
                            return;
                        }
                        if (e.key === "Backspace" && typed === "" && clean.length > 0) {
                            e.preventDefault();
                            remove(clean[clean.length - 1]);
                        }
                    }}
                    onBlur={() => add(typed)}
                    className={cn(FIELD, "sm:max-w-[20rem]")}
                />
                <button
                    type="button"
                    disabled={disabled || typed.trim() === ""}
                    onClick={() => {
                        add(typed);
                        inputRef.current?.focus();
                    }}
                    className={cn(TYPE.tc, "inline-flex h-9 items-center gap-1 rounded-xl px-3 font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-40", FOCUS_RING)}
                >
                    <PlusIcon aria-hidden size={14} /> Add
                </button>
            </div>

            <p id={`${KEYWORDS_FIELD_ID}-note`} className={cn(TYPE.tc, "m-0 mt-1.5", problem ? "text-amber-700" : "text-gray-500")}>
                {problem ??
                    (clean.length === 0
                        ? "None recorded yet."
                        : `${clean.length} of ${KEYWORDS_MAX}${changed ? " · not saved yet" : ""}`)}
            </p>
            {said && (
                <p role="status" className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>
                    {said}
                </p>
            )}
        </div>
    );
}
